import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Divider,
  Grid,
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import { useNotify, useDataProvider } from 'react-admin';
import { tenantService } from '../../services/tenantService';

interface AddMaterialDialogProps {
  open: boolean;
  onClose: () => void;
  taskId: string;
  onSuccess: () => void;
}

interface Material {
  id: string;
  name: string;
  description: string | null
  cost: number;
  unit: string;
}

interface Category {
  id: string;
  name: string;
  description: string | null;
  materials: Material[];
}

export const AddMaterialDialog: React.FC<AddMaterialDialogProps> = ({
  open,
  onClose,
  taskId,
  onSuccess,
}) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [costs, setCosts] = useState<Record<string, string>>({});
  const [manuallyEditedCosts, setManuallyEditedCosts] = useState<Set<string>>(new Set());
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const notify = useNotify();
  const dataProvider = useDataProvider();

  useEffect(() => {
    if (open) {
      fetchCategories();
      setQuantities({});
      setCosts({});
      setManuallyEditedCosts(new Set());
      setSearchQuery('');
    }
  }, [open]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const url = `${baseUrl}/materials/categories?isActive=true`;

      const accessToken = localStorage.getItem('accessToken');
      const headers: HeadersInit = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      };

      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      try {
        const tenantCode = tenantService.getCurrentTenantCode();
        headers['x-tenant-code'] = tenantCode;
      } catch (error) {
        // Tenant service might not be available
      }

      const response = await fetch(url, { headers });
      if (!response.ok) {
        throw new Error(`Failed to fetch categories: ${response.statusText}`);
      }

      const data = await response.json();
      setCategories(data);
    } catch (error: any) {
      notify(error.message || 'Failed to load materials', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = useMemo(() => {
    let processedCategories = categories;

    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      processedCategories = categories.map((category) => ({
        ...category,
        materials: category.materials.filter((material) => {
          const matchesSearch = material.name.toLowerCase().includes(lowerQuery);
          const hasQuantity = parseFloat(quantities[material.id] || '0') > 0;
          const hasCost = parseFloat(costs[material.id] || '0') > 0;
          return matchesSearch || hasQuantity || hasCost;
        }),
      }));
    }

    return processedCategories.filter((category) => category.materials && category.materials.length > 0);
  }, [categories, searchQuery, quantities, costs]);

  const handleQuantityChange = (materialId: string, value: string, materialCost: number) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setQuantities((prev) => ({ ...prev, [materialId]: value }));

      if (!manuallyEditedCosts.has(materialId)) {
        const qty = parseFloat(value);
        if (!isNaN(qty) && materialCost > 0) {
          const newCost = (qty * materialCost).toFixed(2);
          setCosts((prev) => ({ ...prev, [materialId]: newCost }));
        } else if (value === '') {
          setCosts((prev) => ({ ...prev, [materialId]: '' }));
        }
      }
    }
  };

  const handleCostChange = (materialId: string, value: string) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setCosts((prev) => ({ ...prev, [materialId]: value }));
      setManuallyEditedCosts((prev) => new Set(prev).add(materialId));
    }
  };

  const hasUnsavedChanges = useMemo(() => {
    return (
      Object.keys(quantities).some((k) => parseFloat(quantities[k]) > 0) ||
      Object.keys(costs).some((k) => parseFloat(costs[k]) > 0)
    );
  }, [quantities, costs]);

  const handleCloseAttempt = () => {
    if (hasUnsavedChanges) {
      setConfirmCloseOpen(true);
    } else {
      onClose();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const promises: Promise<any>[] = [];

      // Iterate over all materials that have either quantity or cost entered
      const materialIds = new Set([
        ...Object.keys(quantities),
        ...Object.keys(costs),
      ]);

      materialIds.forEach((materialId) => {
        const quantity = parseFloat(quantities[materialId] || '0');
        const cost = parseFloat(costs[materialId] || '0');

        if (quantity > 0 || cost > 0) {
          promises.push(
            dataProvider.create('tasks-materials', {
              data: {
                taskId,
                materialId,
                quantity,
                cost,
              },
            })
          );
        }
      });

      if (promises.length === 0) {
        notify('No materials to add', { type: 'warning' });
        setSaving(false);
        return;
      }

      await Promise.all(promises);
      notify('Materials added successfully', { type: 'success' });
      onSuccess();
    } catch (error: any) {
      notify(error.message || 'Failed to add materials', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseAttempt}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            Add Actuals
            <IconButton onClick={handleCloseAttempt} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, height: '60vh' }}>
          <Box p={2} position="sticky" top={0} bgcolor="background.paper" zIndex={1}>
            <TextField
              fullWidth
              placeholder="Search materials..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear search"
                      onClick={() => setSearchQuery('')}
                      edge="end"
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              size="small"
            />
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box p={2}>
              {filteredCategories.length === 0 ? (
                <Typography textAlign="center" color="text.secondary" py={4}>
                  No materials found
                </Typography>
              ) : (
                filteredCategories.map((category) => (
                  <Box key={category.id} mb={3}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      color="primary"
                      sx={{ px: 1 }}
                    >
                      {category.name}
                    </Typography>
                    {category.description && (
                      <Typography
                        variant="subtitle1"
                        color="primary"
                        sx={{ px: 1, pb: 1 }}
                      >
                        {category.description}
                      </Typography>
                    )}
                    <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                      {category.materials.map((material) => (
                        <Grid item xs={12} key={material.id}>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            p={1}
                            sx={{
                              '&:hover': { bgcolor: 'action.hover' },
                              borderRadius: 1,
                            }}
                          >
                            <Box flex={1}>
                              <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                                {material.name} {material.unit ? `(${material.unit})` : ''}
                              </Typography>

                              {material.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                  {material.description}
                                </Typography>
                              )}

                              {material.cost && material.cost > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  Cost per unit: {material.cost}
                                </Typography>
                              )}
                            </Box>
                            <Box display="flex" gap={2} alignItems="center">
                              <TextField
                                label="Quantity"
                                size="small"
                                value={quantities[material.id] || ''}
                                onChange={(e) =>
                                  handleQuantityChange(material.id, e.target.value, material.cost)
                                }
                                sx={{ width: 100 }}
                                inputProps={{ inputMode: 'decimal' }}
                              />
                              <TextField
                                label="Total Cost"
                                size="small"
                                value={costs[material.id] || ''}
                                onChange={(e) =>
                                  handleCostChange(material.id, e.target.value)
                                }
                                sx={{ width: 100 }}
                                inputProps={{ inputMode: 'decimal' }}
                              />
                            </Box>
                          </Box>
                          <Divider sx={{ my: 0.5 }} />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseAttempt} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !hasUnsavedChanges}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmCloseOpen} onClose={() => setConfirmCloseOpen(false)}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have entered data. Are you sure you want to close without saving?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCloseOpen(false)}>Continue Editing</Button>
          <Button
            onClick={() => {
              setConfirmCloseOpen(false);
              onClose();
            }}
            color="error"
          >
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
